/* eslint-disable no-underscore-dangle */
import { getAuth as getClientAuth } from 'firebase/auth'
import appClient from './client.app'
import {
  firebaseClientInitConfig,
  getTenantId as getFirebaseTenantId,
} from './config'

type TenantIds =
  (typeof firebaseClientInitConfig.tenantIds)[keyof typeof firebaseClientInitConfig.tenantIds]

interface ITenantManager {
  setTenantId(tenantId: TenantIds): Promise<boolean>
  getTenantId(): string | null
  clearTenantId(): Promise<void>
}

class TenantManager implements ITenantManager {
  private _currentOperation: Promise<any> = Promise.resolve()

  private _currentTenantId: TenantIds | null = null

  private readonly storageKey: string = 'tenant'

  constructor() {
    this._currentTenantId = this.readFromStorage()
  }

  public async setTenantId(tenantId: TenantIds): Promise<boolean> {
    await this._currentOperation

    this._currentOperation = (async () => {
      if (
        !Object.values(firebaseClientInitConfig.tenantIds).includes(tenantId)
      ) {
        return false
      }

      this._currentTenantId = tenantId
      localStorage.setItem(this.storageKey, this.obfuscateTenantId(tenantId))

      try {
        const auth = getClientAuth(appClient)
        auth.tenantId = tenantId
      } catch (error) {
        console.error(error)
        return false
      }

      return true
    })()

    return this._currentOperation
  }

  public getTenantId(): TenantIds | null {
    if (this._currentTenantId) {
      return getFirebaseTenantId(this._currentTenantId)
    }

    return this.readFromStorage()
  }

  public async clearTenantId(): Promise<void> {
    await this._currentOperation

    this._currentOperation = (async () => {
      localStorage.removeItem(this.storageKey)
      this._currentTenantId = null
    })()

    return this._currentOperation
  }

  public onTenantChange(
    callback: (tenantId: TenantIds | null) => void
  ): () => void {
    const handler = (event: StorageEvent) => {
      if (event.key === this.storageKey) {
        const newTenantId = this.readFromStorage()
        this._currentTenantId = newTenantId
        callback(newTenantId ?? null)
      }
    }

    window.addEventListener('storage', handler)

    return () => {
      window.removeEventListener('storage', handler)
    }
  }

  private readFromStorage(): TenantIds | null {
    const storedValue = localStorage.getItem(
      this.storageKey
    ) as TenantIds | null

    if (!storedValue) {
      return null
    }
    const deobfuscatedValue = this.deobfuscateTenantId(
      storedValue
    ) as TenantIds | null

    if (
      deobfuscatedValue &&
      Object.values(firebaseClientInitConfig.tenantIds).includes(
        deobfuscatedValue
      )
    ) {
      return deobfuscatedValue
    }

    return null
  }

  private obfuscateTenantId(tenantId: TenantIds): string {
    return window.btoa(`tenant::${tenantId}::${window.location.hostname}`)
  }

  private deobfuscateTenantId(obfuscated: string): TenantIds | null {
    try {
      const decoded = window.atob(obfuscated)
      const matches = decoded.match(/^tenant::(.+)::/)
      return matches ? matches[1] : null
    } catch {
      return null
    }
  }

  public isCurrentTenant(tenantId: TenantIds): boolean {
    return this._currentTenantId === tenantId
  }
}

const tenantManager = new TenantManager()
export default tenantManager
